/**
 * SessionTimeoutProvider component to manage user session timeout and warning.
 * lastActivityTime in session storage is updated on every API call in react/api.js
 * and models.js for Backbone components
 * When the user has been idle for a certain amount of time (SESSION_TIMEOUT_WARNING_MS),
 * a warning modal pops up with a countdown until session expiration (COUNTDOWN_TIME).
 * If the countdown reaches 0, the user is automatically logged out and session strorage is cleared.
 *
 * If the user clicks "Keep your session alive", an API request is sent to refresh the session on the back end,
 * and lastActivityTime in session storage is reset to now.
 *
 * If an API request is made, lastActivityTime is reset, to stay in sync with the Backend session expiration, modal is dismissed,
 * and the idle timer is restarted.
 */

import React, { useEffect, useState, useRef, createContext } from 'react';
import { Typography, Button } from '../components';
import { useLogoutMutation, useLazyKeepSessionAliveQuery } from '../api';
import { setSessionVar, getSessionVar } from '../lib/helpers';

import '../styles/session-timeout-modal.scss';

const SessionTimeoutContext = createContext();

const FE_SESSION_TOKEN_EXPIRATION_MS = 1200000; // 20 min
const SESSION_TIMEOUT_WARNING_MS = 600000; // After 10 minutes, the warning pops up. Change this to a shorter value for testing
const COUNTDOWN_TIME = FE_SESSION_TOKEN_EXPIRATION_MS - SESSION_TIMEOUT_WARNING_MS; // 10 min Timer countdown. This plus the tieout warning equal equal the FE session expiration time of 20 min
const CHECK_INTERVAL_MS = 5000;

export const SessionTimeoutProvider = ({ children }) => {
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [countdownDisplay, setCountdownDisplay] = useState(formatCountdownTime(COUNTDOWN_TIME));
    const countdownRef = useRef(null);
    const intervalRef = useRef(null);

    const [logout] = useLogoutMutation();
    const [triggerKeepSessionAlive] = useLazyKeepSessionAliveQuery();

    const getLastActivityTime = () => {
        const lastActivityTime = getSessionVar('lastActivityTime');

        if (!lastActivityTime) {
            const now = Date.now();
            setSessionVar('lastActivityTime', now);
            return now;
        }
        return parseInt(lastActivityTime, 10);
    };

    const setLastActivityNow = () => {
        setSessionVar('lastActivityTime', Date.now());
    };

    function formatCountdownTime(timeInMilliseconds) {
        const totalSeconds = Math.round(timeInMilliseconds / 1000);
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }

    const keepAlive = async () => {
        try {
            // Send API request to update the session on the back end
            await triggerKeepSessionAlive({ userOrg: sessionStorage.getItem('userOrg') });
            clearInterval(countdownRef.current);
            setShowTimeoutWarning(false);
            setCountdownDisplay(formatCountdownTime(COUNTDOWN_TIME)); // Reset countdown display
            setLastActivityNow(); // Reset last activity time to now
            startIdleTimer(); // Resume idle timer
        } catch (e) {
            // If keep alive fails, log the user out
            await logout();
            localStorage.clear();
            sessionStorage.clear();
            document.location.href = '/';
        }
    };

    const startCountdown = () => {
        // Countdown expires at 10 min after this function is called
        const expiresAt = Date.now() + COUNTDOWN_TIME;
        setShowTimeoutWarning(true);
        clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
            const now = Date.now();
            const lastActivity = getLastActivityTime();

            // If an API call was made during the countdown window, lastActivity will have been updated and the backend session was extended.
            // Dismiss the modal and restart the idle timer, to stay in sync with the backend.
            if (now - lastActivity < SESSION_TIMEOUT_WARNING_MS) {
                clearInterval(countdownRef.current);
                setShowTimeoutWarning(false);
                setCountdownDisplay(formatCountdownTime(COUNTDOWN_TIME));
                startIdleTimer();
                return;
            }

            // Always derive countDownTime from real clock values inside setInterval and not a variable decremented inside the setInterval.
            // If the user switches browser tabs, setInterval may be throttled, which would show an inaccurate countown time.
            const countdownTime = expiresAt - now;

            if (countdownTime <= 0) {
                clearInterval(countdownRef.current);

                // Using an async IIFE here so we can await the logout request
                (async () => {
                    try {
                        await logout();
                        // Session timed out - clear storage and redirect to login
                        localStorage.clear();
                        sessionStorage.clear();
                        document.location.href = '/';
                    } catch (e) {
                        // Session timed out but logout failed - still clear storage and redirect
                        localStorage.clear();
                        sessionStorage.clear();
                        document.location.href = '/?status=session_timeout';
                    }
                })();
            } else {
                setCountdownDisplay(formatCountdownTime(countdownTime));
            }
        }, 1000);
    };

    // Check for idle user, starts once this component loads
    // Checks every CHECK_INTERVAL_MS seconds
    const startIdleTimer = () => {
        clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
            const now = Date.now();
            const lastActivity = getLastActivityTime();

            // Display and start the countdown timer if the user has been idle past the timeout warning value
            if (now - lastActivity >= SESSION_TIMEOUT_WARNING_MS) {
                clearInterval(intervalRef.current);
                startCountdown();
            }
        }, CHECK_INTERVAL_MS);
    };

    useEffect(() => {
        setLastActivityNow();
        startIdleTimer();

        return () => {
            clearInterval(intervalRef.current);
            clearInterval(countdownRef.current);
        };
    }, []);

    return (
        <SessionTimeoutContext.Provider value={{ keepAlive }}>
            {children}
            {showTimeoutWarning && (
                <div className="session-timeout-modal">
                    <Typography.Paragraph>Due to inactivity, you will be logged out in:</Typography.Paragraph>
                    <Typography.Paragraph className="countdown" type="danger">
                        {countdownDisplay}
                    </Typography.Paragraph>
                    <Button type="primary" onClick={keepAlive}>
                        Keep your session alive
                    </Button>
                </div>
            )}
        </SessionTimeoutContext.Provider>
    );
};
